import {
  checkRestrictions, combinePeriods, stayPossibleFromPeriods, weekdayOf,
  type Periods, type Restriction,
} from '@availability/index';
import type { CalendarWindow, IsoRule, IsoWindow } from './api';
import { toDay, type Day } from './dates';

/**
 * What a guest may actually book, answered locally.
 *
 * ---------------------------------------------------------------------------
 * THE TWO QUESTIONS A DATE-RANGE CALENDAR ASKS
 * ---------------------------------------------------------------------------
 *
 * `canArrive(day)`        before anything is chosen. Greys the dates no stay
 *                         of any length can begin on.
 * `canDepart(a, day)`     after an arrival is chosen. Greys the departures
 *                         that arrival cannot reach.
 *
 * They are different questions and they are answered from different things,
 * which is the whole reason `Periods` has two fields:
 *
 *   closed      nothing is free. Overlapping one at all is impossible, so a
 *               closed night can be greyed with nothing else known.
 *   switching   units are free at different times, so a stay fits unless it
 *               SPANS the window. Nothing here can be greyed up front — it
 *               constrains how far a departure may go, and only from a given
 *               arrival.
 *
 * Get that backwards and a calendar either goes dark on dates the park can
 * sell, or offers dates it cannot sell on any terms.
 *
 * ---------------------------------------------------------------------------
 * WHY THE FILTERED SET IS FOLDED HERE AND NOT AT THE API
 * ---------------------------------------------------------------------------
 *
 * A guest filtering on "own sanitary facilities" asks about one of 2^n subsets
 * of the park, and the server cannot anticipate them. So it sends one period
 * set per type and `combinePeriods` folds any subset into one answer in the
 * browser — the closure property that makes a filter change a render rather
 * than a request.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT REFUSES TO GUESS
 * ---------------------------------------------------------------------------
 *
 * Outside the fetched window it answers `null`, never `false`. A calendar
 * scrolled past what was fetched must ask for more rather than draw a month of
 * grey — absence of knowledge is not absence of availability, and the two look
 * identical on screen.
 */

/** One type's availability, in day numbers. */
interface TypeStays {
  id: string;
  /** Null where the park enumerates no units: unknown, not free. */
  periods: Periods | null;
  rules: Restriction[];
}

export interface Stays {
  /** The range this knows about. Half-open, in day numbers. */
  from: Day;
  until: Day;
  maxNights: number;
  types: TypeStays[];
}

const window_ = (w: IsoWindow) => ({ from: toDay(w.from)!, until: toDay(w.until)! });

const rule = (r: IsoRule): Restriction => ({
  from: toDay(r.from)!,
  until: toDay(r.until)!,
  ...(r.arriveOn ? { arriveOn: r.arriveOn } : {}),
  ...(r.departOn ? { departOn: r.departOn } : {}),
  ...(r.minNights != null ? { minNights: r.minNights } : {}),
  ...(r.maxNights != null ? { maxNights: r.maxNights } : {}),
});

export function toStays(body: CalendarWindow): Stays {
  return {
    from: toDay(body.from)!,
    until: toDay(body.until)!,
    maxNights: body.maxNights,
    types: body.accommodations.map((a) => ({
      id: a.id,
      periods: a.periods === null ? null : {
        closed: a.periods.closed.map(window_),
        switching: a.periods.switching.map(window_),
      },
      /**
       * ONLY the rules that actually forbid a stay.
       *
       * DEFECT: all three kinds were treated alike, so the calendar greyed out
       * dates the park sells. A maximum-stay rule whose effect is `contact` —
       * ring reception for longer than a week — made a three-week camping
       * holiday unselectable; the Ascension and Whitsun weekends are
       * `indicative` with an arrival weekday attached, so every arrival across
       * both was withdrawn. The park had said "ask us" and its own site
       * answered "no" — the one direction of wrong that costs it money.
       *
       * A `contact` or `indicative` stay stays on the calendar and is priced;
       * what it is worth is decided by the quote, which carries the park's own
       * words for it. Nothing is claimed here that the quote will not confirm.
       */
      rules: a.rules.filter((r) => (r.effect ?? 'block') === 'block').map(rule),
    })),
  };
}

/**
 * The stays available across a set of types, as one answer.
 *
 * A type with `periods: null` is UNKNOWN rather than free, and an unknown type
 * cannot make a date impossible — the park has not said it is full, so the
 * honest answer for a set containing one is that a stay may be possible. It is
 * therefore treated as unconstrained here, exactly as `combinePeriods` treats a
 * type nothing blocks, and the "ask us" wording on its card is what tells the
 * guest the availability is not being asserted.
 */
export interface Combined {
  periods: Periods;
  /** Rules of the narrowed set, all of which must be satisfied. */
  rules: Restriction[];
  from: Day;
  until: Day;
  maxNights: number;
}

export function combineFor(stays: Stays, ids: readonly string[]): Combined {
  const chosen = stays.types.filter((t) => ids.includes(t.id));
  const known = chosen.filter((t) => t.periods !== null).map((t) => t.periods!);

  return {
    // An unknown type among them means nothing is provably impossible, which
    // `combinePeriods` already expresses for a type with no windows.
    periods: known.length < chosen.length
      ? { closed: [], switching: [] }
      : combinePeriods(known, stays.from, stays.until),
    // The UNION of the set's rules would be wrong in the other direction: a
    // stay is offerable if ANY type in the set can take it, so a rule is only
    // binding when every type carries an equivalent one. Rather than intersect
    // rule objects — which have no meaningful intersection — the per-type test
    // below is what decides, and this list is only for the single-type case.
    rules: chosen.length === 1 ? chosen[0]!.rules : [],
    from: stays.from,
    until: stays.until,
    maxNights: stays.maxNights,
  };
}

/** Whether a type can take this exact stay: availability AND its own rules. */
function typeTakes(t: TypeStays, from: Day, nights: number): boolean {
  if (t.periods !== null && !stayPossibleFromPeriods(t.periods, from, nights)) return false;
  return checkRestrictions(t.rules, from, nights) === null;
}

/**
 * The lengths worth trying from a date, before any of them is tested.
 *
 * Null means none: an arrival weekday the rules forbid rules out EVERY length
 * at once, and that is the common case rather than a corner — a Saturday-only
 * changeover makes six days in seven categorically unarrivable. Discovering
 * that by trying 180 lengths, for every type, for every cell of a
 * three-month grid, is a few hundred thousand pointless tests on a phone.
 */
function lengthsWorthTrying(t: TypeStays, day: Day): { min: number; max: number } | null {
  let min = 1;
  let max = Infinity;
  for (const r of t.rules) {
    if (day < r.from || day >= r.until) continue;
    if (r.arriveOn && !r.arriveOn.includes(weekdayOf(day))) return null;
    if (r.minNights != null) min = Math.max(min, r.minNights);
    if (r.maxNights != null) max = Math.min(max, r.maxNights);
  }
  return min > max ? null : { min, max };
}

/**
 * The first night at or after `day` that nothing is free on.
 *
 * A stay reaching it is impossible, and so is every longer one — `closed`
 * rejects any overlap — so this is a hard ceiling on the lengths worth testing
 * rather than a hint. Infinity when the horizon holds no closure.
 */
function firstClosedFrom(periods: Periods | null, day: Day): number {
  if (periods === null) return Infinity;
  let best = Infinity;
  for (const w of periods.closed) {
    if (w.until <= day) continue;
    if (w.from <= day) return day;
    if (w.from < best) best = w.from;
  }
  return best;
}

/**
 * Can a stay of any length begin here?
 *
 * Null outside the fetched window. `true` when SOME length works for SOME type
 * in the set — the guest has not chosen a length yet, so the calendar must not
 * grey a date merely because one length fails on it.
 */
export function canArrive(stays: Stays, ids: readonly string[], day: Day): boolean | null {
  if (day < stays.from || day >= stays.until) return null;
  const chosen = stays.types.filter((t) => ids.includes(t.id));
  if (chosen.length === 0) return false;

  const horizon = Math.min(stays.maxNights, stays.until - day);
  for (const t of chosen) {
    const bounds = lengthsWorthTrying(t, day);
    if (bounds === null) continue;
    // A closure the stay would reach ends every longer stay too.
    const ceiling = Math.min(horizon, bounds.max, firstClosedFrom(t.periods, day) - day);
    for (let n = bounds.min; n <= ceiling; n += 1) if (typeTakes(t, day, n)) return true;
  }
  return false;
}

/**
 * Can the guest leave on this day, having arrived on `arrival`?
 *
 * Null outside the window. A departure equal to or before the arrival is not a
 * stay and is refused rather than reinterpreted — the CALLER decides that a
 * click before the arrival starts the selection again, because that is an
 * interaction decision and this file only knows about availability.
 */
export function canDepart(
  stays: Stays, ids: readonly string[], arrival: Day, day: Day,
): boolean | null {
  if (day <= arrival) return false;
  if (day > stays.until) return null;
  const nights = day - arrival;
  if (nights > stays.maxNights) return false;

  const chosen = stays.types.filter((t) => ids.includes(t.id));
  return chosen.some((t) => typeTakes(t, arrival, nights));
}

/**
 * Every departure reachable from an arrival, as a set.
 *
 * Built once per arrival rather than asked per cell: a three-month grid is 90
 * cells and each `canDepart` walks every type's windows, so the difference is
 * a visible one on a phone.
 */
export function departuresFrom(
  stays: Stays, ids: readonly string[], arrival: Day,
): Set<Day> {
  const out = new Set<Day>();
  const chosen = stays.types.filter((t) => ids.includes(t.id));
  const horizon = Math.min(stays.maxNights, stays.until - arrival);

  for (const t of chosen) {
    const bounds = lengthsWorthTrying(t, arrival);
    if (bounds === null) continue;
    const ceiling = Math.min(horizon, bounds.max, firstClosedFrom(t.periods, arrival) - arrival);
    for (let n = bounds.min; n <= ceiling; n += 1) {
      if (!out.has(arrival + n) && typeTakes(t, arrival, n)) out.add(arrival + n);
    }
  }
  return out;
}

/**
 * Arrivals in a range, as a set. Dates outside the window are simply absent.
 *
 * `nights` narrows it to one length, which is NOT the default and is only for a
 * host that has fixed the stay — an embed for weekly rentals, a page selling a
 * three-night break. There the old question is the right one, because a date
 * that cannot take THAT length is a date the guest must not be offered; with no
 * fixed length the question is "can any stay begin here", and asking about a
 * length the guest has not chosen is what the two-field flow exists to stop.
 */
export function arrivalsIn(
  stays: Stays, ids: readonly string[], from: Day, until: Day, nights?: number | null,
): Set<Day> {
  const out = new Set<Day>();
  for (let d = Math.max(from, stays.from); d < Math.min(until, stays.until); d += 1) {
    const ok = nights == null
      ? canArrive(stays, ids, d) === true
      : canDepart(stays, ids, d, d + nights) === true;
    if (ok) out.add(d);
  }
  return out;
}

/** Whether `stays` covers everything from `from` to `until`. */
export function covers(stays: Stays | null, from: Day, until: Day): boolean {
  return stays !== null && stays.from <= from && stays.until >= until;
}
